import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  MessageSquare,
  Users,
  Cpu,
  ToggleRight,
  FileText,
  Save,
  RefreshCw,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle,
  Baby,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

// Stat Card Component
interface StatCardProps {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
  subtext?: string;
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label, subtext }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-600">{label}</div>
          {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
        </div>
      </div>
    </div>
  );
}

// Section Header Component
interface SectionHeaderProps {
  icon: React.ElementType;
  title: string;
  description?: string;
}

function SectionHeader({ icon: Icon, title, description }: SectionHeaderProps) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-slate-600" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
    </div>
  );
}

// Toggle Component
interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function Toggle({ label, description, checked, onChange, disabled }: ToggleProps) {
  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
      <div className="flex-1">
        <span className="font-medium text-slate-900">{label}</span>
        {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <div className={`w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
      </label>
    </div>
  );
}

// Model options
const modelOptions = [
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', description: 'Schnell & günstig' },
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4', description: 'Ausgewogen' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4', description: 'Beste Qualität' },
];

export function AIChatAdminPage() {
  const queryClient = useQueryClient();

  // Local state for settings
  const [model, setModel] = useState('claude-haiku-4-5-20251001');
  const [maxTokens, setMaxTokens] = useState(1024);
  const [chatMode, setChatMode] = useState<'mock' | 'api'>('api');

  // Context toggles
  const [contextPartner, setContextPartner] = useState(true);
  const [contextAppointments, setContextAppointments] = useState(true);
  const [contextRisk, setContextRisk] = useState(true);
  const [contextBabyNames, setContextBabyNames] = useState(true);
  const [contextBabyMode, setContextBabyMode] = useState(true);

  // UI state
  const [showBabyPrompt, setShowBabyPrompt] = useState(false);

  // Fetch current settings
  const { data: settings } = useQuery({
    queryKey: ['ai-chat-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [
          'ai_model',
          'ai_max_tokens',
          'ai_chat_mode',
          'ai_context_partner',
          'ai_context_appointments',
          'ai_context_risk',
          'ai_context_baby_names',
          'ai_context_baby_mode',
        ]);

      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      data?.forEach((s: { key: string; value: string }) => {
        settingsMap[s.key] = s.value;
      });
      return settingsMap;
    },
  });

  // Update local state when settings load
  useEffect(() => {
    if (settings) {
      if (settings.ai_model) setModel(settings.ai_model);
      if (settings.ai_max_tokens) setMaxTokens(parseInt(settings.ai_max_tokens) || 1024);
      if (settings.ai_chat_mode) setChatMode(settings.ai_chat_mode as 'mock' | 'api');
      if (settings.ai_context_partner !== undefined) setContextPartner(settings.ai_context_partner === 'true');
      if (settings.ai_context_appointments !== undefined) setContextAppointments(settings.ai_context_appointments === 'true');
      if (settings.ai_context_risk !== undefined) setContextRisk(settings.ai_context_risk === 'true');
      if (settings.ai_context_baby_names !== undefined) setContextBabyNames(settings.ai_context_baby_names === 'true');
      if (settings.ai_context_baby_mode !== undefined) setContextBabyMode(settings.ai_context_baby_mode === 'true');
    }
  }, [settings]);

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['ai-chat-stats'],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [conversationsResult, messagesResult, usersResult, todayResult] = await Promise.all([
        supabase.from('ai_conversations').select('id', { count: 'exact', head: true }),
        supabase.from('ai_messages').select('id', { count: 'exact', head: true }),
        supabase.from('ai_conversations').select('user_id'),
        supabase.from('ai_messages').select('id', { count: 'exact', head: true }).gte('created_at', today.toISOString()),
      ]);

      const uniqueUsers = new Set((usersResult.data as { user_id: string }[] | null)?.map((u) => u.user_id));

      return {
        totalConversations: conversationsResult.count || 0,
        totalMessages: messagesResult.count || 0,
        uniqueUsers: uniqueUsers.size,
        messagesToday: todayResult.count || 0,
      };
    },
  });

  // Save settings mutation
  const saveSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await (supabase
        .from('system_settings') as any)
        .upsert({ key, value }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-chat-settings'] });
    },
  });

  // Save all settings
  const handleSaveSettings = async () => {
    try {
      await Promise.all([
        saveSetting.mutateAsync({ key: 'ai_model', value: model }),
        saveSetting.mutateAsync({ key: 'ai_max_tokens', value: maxTokens.toString() }),
        saveSetting.mutateAsync({ key: 'ai_chat_mode', value: chatMode }),
        saveSetting.mutateAsync({ key: 'ai_context_partner', value: contextPartner.toString() }),
        saveSetting.mutateAsync({ key: 'ai_context_appointments', value: contextAppointments.toString() }),
        saveSetting.mutateAsync({ key: 'ai_context_risk', value: contextRisk.toString() }),
        saveSetting.mutateAsync({ key: 'ai_context_baby_names', value: contextBabyNames.toString() }),
        saveSetting.mutateAsync({ key: 'ai_context_baby_mode', value: contextBabyMode.toString() }),
      ]);
      toast.success('Einstellungen gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  // Get model info
  const currentModelInfo = modelOptions.find((m) => m.value === model);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center">
              <Bot className="w-7 h-7 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">KI-Chat</h1>
              <p className="text-slate-500">Einstellungen und Übersicht für den KI-Assistenten</p>
            </div>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saveSetting.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50"
          >
            {saveSetting.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Speichern
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={MessageSquare}
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
          value={stats?.totalConversations || 0}
          label="Konversationen"
        />
        <StatCard
          icon={TrendingUp}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          value={stats?.totalMessages || 0}
          label="Nachrichten gesamt"
        />
        <StatCard
          icon={Users}
          iconBg="bg-teal-100"
          iconColor="text-teal-600"
          value={stats?.uniqueUsers || 0}
          label="Aktive Nutzer"
        />
        <StatCard
          icon={Clock}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          value={stats?.messagesToday || 0}
          label="Nachrichten heute"
        />
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader
            icon={Cpu}
            title="Modell-Einstellungen"
            description="KI-Modell und Parameter"
          />

          <div className="space-y-4">
            {/* Mode Toggle */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-900">Modus</span>
                <div className="flex items-center gap-2">
                  {chatMode === 'api' ? (
                    <span className="flex items-center gap-1 text-sm text-emerald-600">
                      <CheckCircle className="w-4 h-4" />
                      API aktiv
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-amber-600">
                      <AlertCircle className="w-4 h-4" />
                      Demo-Modus
                    </span>
                  )}
                </div>
              </div>
              <select
                value={chatMode}
                onChange={(e) => setChatMode(e.target.value as 'mock' | 'api')}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                <option value="api">API (Claude)</option>
                <option value="mock">Demo (Mock-Antworten)</option>
              </select>
            </div>

            {/* Model Selection */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-900">Modell</span>
                {currentModelInfo && (
                  <span className="text-xs text-slate-500">{currentModelInfo.description}</span>
                )}
              </div>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Max Tokens */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-900">Max. Tokens</span>
                <span className="text-sm text-slate-500">{maxTokens}</span>
              </div>
              <input
                type="range"
                min="256"
                max="4096"
                step="256"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>256</span>
                <span>4096</span>
              </div>
            </div>

            {/* Limits Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Hinweis:</strong> Die Limits für KI-Anfragen pro Monat werden unter{' '}
                <a href="/features/limits" className="underline hover:text-blue-900">
                  Funktionen → Verwaltung → Limits
                </a>{' '}
                für kostenlose und Premium-Nutzer separat konfiguriert.
              </p>
            </div>
          </div>
        </div>

        {/* Context Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader
            icon={ToggleRight}
            title="Kontext-Bausteine"
            description="Welche Informationen soll die KI nutzen?"
          />

          <div className="space-y-3">
            <Toggle
              label="Partner-Informationen"
              description="Name und Rolle des Partners einbeziehen"
              checked={contextPartner}
              onChange={setContextPartner}
            />
            <Toggle
              label="Termine"
              description="Anstehende Arzttermine & Vorsorge"
              checked={contextAppointments}
              onChange={setContextAppointments}
            />
            <Toggle
              label="Risikoschwangerschaft"
              description="Details zu Risikofaktoren anzeigen"
              checked={contextRisk}
              onChange={setContextRisk}
            />
            <Toggle
              label="Baby-Namen"
              description="Gewählte Namen für das Baby"
              checked={contextBabyNames}
              onChange={setContextBabyNames}
            />
            <Toggle
              label="Baby-Modus Kontext"
              description="Baby-spezifischer Kontext (Alter, Wachstum, Warnsignale)"
              checked={contextBabyMode}
              onChange={setContextBabyMode}
            />
          </div>

          {/* Info Box */}
          <div className="mt-4 p-4 bg-violet-50 rounded-lg border border-violet-100">
            <p className="text-sm text-violet-700">
              <strong>Hinweis:</strong> Diese Einstellungen beeinflussen den System-Prompt der KI.
              Mehr Kontext ermöglicht personalisiertere Antworten, erhöht aber auch den Token-Verbrauch.
            </p>
          </div>
        </div>
      </div>

      {/* System Prompt Preview */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <SectionHeader
          icon={FileText}
          title="System-Prompt Vorschau"
          description="Basis-Anweisungen für die KI (read-only)"
        />

        {/* Toggle between pregnancy and baby mode */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setShowBabyPrompt(false)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !showBabyPrompt
                ? 'bg-teal-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            🤰 Schwangerschafts-Modus
          </button>
          <button
            onClick={() => setShowBabyPrompt(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showBabyPrompt
                ? 'bg-pink-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Baby className="w-4 h-4" />
            Baby-Modus
          </button>
        </div>

        <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
{showBabyPrompt ? `Du bist ein Baby- und Eltern-Berater für die App ParentPath.
Das Baby heißt [Name], ist [Geschlecht] und [Alter] alt.

WICHTIGE REGELN:
1. Du gibst KEINE medizinischen Diagnosen
2. Du empfiehlst KEINE Medikamente (außer Verweis zum Arzt/Apotheker)
3. Bei Warnsignalen: "Bitte kontaktiere umgehend euren Kinderarzt"
4. Du bist freundlich und empathisch

BABY-WARNSIGNALE (IMMER zum Arzt):
- Fieber über 38°C bei Neugeborenen (unter 3 Monate)
- Fieber über 39°C bei älteren Babys
- Dehydrierung (trockene Windeln, eingefallene Fontanelle)
- Atemnot oder blaue Lippen
- Anhaltende Nahrungsverweigerung
- Starkes, anhaltendes Schreien
- Ausschlag mit Fieber
- Krampfanfälle
- Lethargie / nicht weckbar

Beantworte Fragen zu:
- Baby-Symptomen (Koliken, Ausschlag, Erkältung)
- Stillen und Flasche
- Schlaf und Beruhigung
- Entwicklungsmeilensteine
- Ernährung und Beikost
- Pflege und Hygiene

${contextBabyMode ? '✓ Baby-Kontext aktiviert (Alter, Wachstum, Messungen)' : '✗ Baby-Kontext deaktiviert'}
${contextPartner ? '✓ Partner-Informationen werden einbezogen' : '✗ Partner-Informationen deaktiviert'}
${contextAppointments ? '✓ Termine werden einbezogen' : '✗ Termine deaktiviert'}
${contextBabyNames ? '✓ Baby-Namen werden einbezogen' : '✗ Baby-Namen deaktiviert'}` : `Du bist ein freundlicher, einfühlsamer KI-Assistent für die Schwangerschafts-App "ParentPath".

Deine Hauptaufgaben:
- Fragen zur Schwangerschaft beantworten
- Emotionale Unterstützung bieten
- Allgemeine Tipps geben

Wichtige Regeln:
- Keine medizinischen Diagnosen stellen
- Bei Notfällen sofort an Arzt/Notruf verweisen
- Auf deutscher Sprache antworten
- Warmherzig und verständnisvoll kommunizieren

${contextPartner ? '✓ Partner-Informationen werden einbezogen' : '✗ Partner-Informationen deaktiviert'}
${contextAppointments ? '✓ Termine werden einbezogen' : '✗ Termine deaktiviert'}
${contextRisk ? '✓ Risiko-Details werden einbezogen' : '✗ Risiko-Details deaktiviert'}
${contextBabyNames ? '✓ Baby-Namen werden einbezogen' : '✗ Baby-Namen deaktiviert'}`}
          </pre>
        </div>
      </div>
    </div>
  );
}
